import { supabase } from './client';

/** Generate the next broadcast ID in sequence (VB001, VB002, ...) */
async function generateNextBroadcastId(): Promise<string> {
  const { data, error } = await supabase
    .from('matches')
    .select('broadcast_id')
    .order('broadcast_id', { ascending: false })
    .limit(1);

  if (error) throw error;

  if (!data || data.length === 0 || !data[0].broadcast_id) {
    return 'VB001';
  }

  const lastId = data[0].broadcast_id;
  const seq = parseInt(lastId.replace('VB', ''), 10);
  return `VB${String(seq + 1).padStart(3, '0')}`;
}

export async function createMatch(matchData: {
  matchNumber: number;
  date: string;
  matchType: string;
  teamAId: string;
  teamBId: string;
  tossWonById: string;
  tossChoice: string;
  tournamentId?: string | null;
  winningPoint?: number | null;
  goldenPoint?: number | null;
  serveRotationCooloff?: number;
}) {
  // BUG-M06 fix: retry on broadcast_id unique constraint collision (race condition)
  for (let attempt = 0; attempt < 3; attempt++) {
    const broadcastId = await generateNextBroadcastId();
    const { data, error } = await supabase
      .from('matches')
      .insert({
        match_number: matchData.matchNumber,
        date: matchData.date,
        match_type: matchData.matchType,
        team_a_id: matchData.teamAId,
        team_b_id: matchData.teamBId,
        toss_won_by_id: matchData.tossWonById,
        toss_choice: matchData.tossChoice,
        tournament_id: matchData.tournamentId || null,
        winning_point: matchData.winningPoint || null,
        golden_point: matchData.goldenPoint || null,
        serve_rotation_cooloff: matchData.serveRotationCooloff ?? 5,
        status: 'first-server',
        broadcast_id: broadcastId,
      })
      .select()
      .single();

    if (error) {
      if (attempt < 2 && error.code === '23505') continue; // retry on duplicate broadcast_id
      throw error;
    }
    return data;
  }
  throw new Error('Failed to generate unique broadcast ID after retries');
}

export async function scheduleMatch(matchData: {
  matchNumber: number;
  date: string;
  matchTime?: string | null;
  matchType: string;
  teamAId: string;
  teamBId: string;
  tournamentId: string;
  winningPoint?: number | null;
  goldenPoint?: number | null;
  liveStreamUrl?: string | null;
}) {
  // BUG-M06 fix: retry on broadcast_id unique constraint collision (race condition)
  for (let attempt = 0; attempt < 3; attempt++) {
    const broadcastId = await generateNextBroadcastId();
    const { data, error } = await supabase
      .from('matches')
      .insert({
        match_number: matchData.matchNumber,
        date: matchData.date,
        match_time: matchData.matchTime || null,
        match_type: matchData.matchType,
        team_a_id: matchData.teamAId,
        team_b_id: matchData.teamBId,
        tournament_id: matchData.tournamentId,
        winning_point: matchData.winningPoint || null,
        golden_point: matchData.goldenPoint || null,
        status: 'scheduled',
        broadcast_id: broadcastId,
        live_stream_url: matchData.liveStreamUrl || null,
      })
      .select()
      .single();

    if (error) {
      if (attempt < 2 && error.code === '23505') continue; // retry on duplicate broadcast_id
      throw error;
    }
    return data;
  }
  throw new Error('Failed to generate unique broadcast ID after retries');
}

export async function updateScheduledMatch(matchId: string, matchData: {
  matchNumber: number;
  date: string;
  matchTime?: string | null;
  matchType: string;
  teamAId: string;
  teamBId: string;
  winningPoint?: number | null;
  goldenPoint?: number | null;
  liveStreamUrl?: string | null;
}) {
  const { data, error } = await supabase
    .from('matches')
    .update({
      match_number: matchData.matchNumber,
      date: matchData.date,
      match_time: matchData.matchTime || null,
      match_type: matchData.matchType,
      team_a_id: matchData.teamAId,
      team_b_id: matchData.teamBId,
      winning_point: matchData.winningPoint || null,
      golden_point: matchData.goldenPoint || null,
      live_stream_url: matchData.liveStreamUrl || null,
    })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateMatchLiveStreamUrl(matchId: string, liveStreamUrl: string | null) {
  const { data, error } = await supabase
    .from('matches')
    .update({ live_stream_url: liveStreamUrl || null })
    .eq('id', matchId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function startScheduledMatch(matchId: string, tossWonById: string, tossChoice: string, serveRotationCooloff: number = 5) {
  const { data, error } = await supabase
    .from('matches')
    .update({
      toss_won_by_id: tossWonById,
      toss_choice: tossChoice,
      serve_rotation_cooloff: serveRotationCooloff,
      status: 'first-server',
      start_time: new Date().toISOString(),
    })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function setMatchStartTime(matchId: string) {
  const { error } = await supabase
    .from('matches')
    .update({ start_time: new Date().toISOString() })
    .eq('id', matchId)
    .is('start_time', null);
  if (error) throw error;
}

export async function updateMatchStatus(matchId: string, status: string, winnerId?: string, endTime?: string) {
  const updateData: Record<string, unknown> = { status };
  if (winnerId) {
    updateData.winner_id = winnerId;
  }
  if (endTime) {
    updateData.end_time = endTime;
  }

  const { data, error } = await supabase
    .from('matches')
    .update(updateData)
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getTournamentData(tournamentId?: string) {
  let query = supabase
    .from('matches')
    .select(`
      id,
      match_number,
      date,
      match_type,
      tournament_id,
      team_a_id,
      team_b_id,
      toss_won_by_id,
      toss_choice,
      status,
      winner_id,
      player_of_match_ids,
      player_of_match_team_id,
      sets (
        id,
        set_number,
        score_a,
        score_b,
        winner_id,
        rallies (
          rally_number,
          serving_team_id,
          server_id,
          point_won_by_id,
          lost_reason,
          point_winner_id,
          categories,
          recorded_at
        )
      )
    `);

  if (tournamentId) {
    query = query.eq('tournament_id', tournamentId);
  }

  const { data, error } = await query.order('match_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getMatchWithDetails(matchId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        *,
        rallies (
          id,
          set_id,
          rally_number,
          serving_team_id,
          server_id,
          point_won_by_id,
          lost_reason,
          point_winner_id,
          categories,
          recorded_at,
          is_server_fault
        )
      )
    `)
    .eq('id', matchId)
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .single();

  if (error) throw error;
  return data;
}

// Lightweight scoreboard poll: match + sets WITHOUT rallies.
// Used by the public scoreboard so initial/periodic loads don't fetch all
// rally data. Rallies are fetched lazily per-set (see getRalliesBySetForDisplay).
export async function getMatchScoreboardSummary(matchId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        id,
        match_id,
        set_number,
        score_a,
        score_b,
        winner_id,
        started_at,
        ended_at,
        side_change_shown,
        current_serving_team_id,
        current_server_id
      )
    `)
    .eq('id', matchId)
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .single();

  if (error) throw error;
  return data;
}

export async function getMatchByBroadcastId(broadcastId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      sets (
        *,
        rallies (
          id,
          rally_number,
          serving_team_id,
          server_id,
          point_won_by_id,
          lost_reason
        )
      ),
      timeouts (
        id,
        set_number,
        team_id,
        started_at,
        ended_at,
        duration_seconds
      )
    `)
    .eq('broadcast_id', broadcastId)
    .neq('status', 'discarded')
    .order('set_number', { referencedTable: 'sets', ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getLiveMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_time, match_type, team_a_id, team_b_id, status, tournament_id, start_time, end_time, created_at'
    )
    .in('status', ['live', 'set-won', 'first-server'])
    .order('match_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getScheduledMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_time, match_type, team_a_id, team_b_id, status, tournament_id, created_at'
    )
    .eq('status', 'scheduled')
    .order('match_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getCompletedMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_time, match_type, team_a_id, team_b_id, status, tournament_id, start_time, end_time, created_at'
    )
    .eq('status', 'match-won')
    .order('match_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getDiscardedMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select(
      'id, match_number, date, match_type, team_a_id, team_b_id, status, tournament_id, created_at'
    )
    .eq('status', 'discarded')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function discardMatch(matchId: string): Promise<void> {
  // Fetch current state before update
  const { data: before } = await supabase
    .from('matches')
    .select('id, status')
    .eq('id', matchId)
    .maybeSingle();

  console.log('[discardMatch] Before:', { matchId, currentStatus: before?.status });

  const { error } = await supabase
    .from('matches')
    .update({ status: 'discarded' })
    .eq('id', matchId);

  if (error) {
    console.error('[discardMatch] DB error:', error);
    throw error;
  }

  console.log('[discardMatch] After: status set to discarded for', matchId);
}

async function deleteMatchCascade(matchId: string): Promise<{ ralliesDeleted: number; setsDeleted: number }> {
  const { data: sets } = await supabase.from('sets').select('id').eq('match_id', matchId);
  let ralliesDeleted = 0;
  const setsDeleted = sets?.length ?? 0;
  if (sets && sets.length > 0) {
    for (const set of sets) {
      const { data: rallies } = await supabase.from('rallies').select('id').eq('set_id', set.id);
      if (rallies && rallies.length > 0) {
        await supabase.from('rallies').delete().eq('set_id', set.id);
        ralliesDeleted += rallies.length;
      }
    }
    await supabase.from('sets').delete().eq('match_id', matchId);
  }
  return { ralliesDeleted, setsDeleted };
}

export async function deleteMatchHard(matchId: string): Promise<void> {
  const cascade = await deleteMatchCascade(matchId);
  const { error } = await supabase.from('matches').delete().eq('id', matchId);
  if (error) throw error;
  console.log('[deleteMatchHard]', { matchId, ...cascade });
}

export async function getAllMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select('id, match_number, broadcast_id, date, match_type, team_a_id, team_b_id, status, tournament_id, created_at')
    .order('match_number', { ascending: false });
  if (error) throw error;
  return data;
}

export async function deleteMatchesByStatus(status: string): Promise<number> {
  const { data: matches } = await supabase.from('matches').select('id').eq('status', status);
  if (!matches || matches.length === 0) return 0;
  for (const m of matches) await deleteMatchCascade(m.id);
  const { error } = await supabase.from('matches').delete().eq('status', status);
  if (error) throw error;
  console.log('[deleteMatchesByStatus]', { status, count: matches.length });
  return matches.length;
}

export async function deleteAllMatchesHard(): Promise<number> {
  const { data: matches } = await supabase.from('matches').select('id');
  if (!matches || matches.length === 0) return 0;
  for (const m of matches) await deleteMatchCascade(m.id);
  const { error } = await supabase.from('matches').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  if (error) throw error;
  console.log('[deleteAllMatchesHard]', { count: matches.length });
  return matches.length;
}

/** Find orphan matches: live/awaiting matches without a tournament_id */
export async function getOrphanMatches() {
  const { data, error } = await supabase
    .from('matches')
    .select('id, match_number, date, match_type, team_a_id, team_b_id, status, created_at')
    .in('status', ['live', 'set-won', 'first-server'])
    .is('tournament_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/** Assign a tournament to an orphan match */
export async function assignMatchToTournament(matchId: string, tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .update({ tournament_id: tournamentId })
    .eq('id', matchId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getMatchesByTournament(tournamentId: string) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      id,
      match_number,
      broadcast_id,
      date,
      match_time,
      match_type,
      team_a_id,
      team_b_id,
      toss_won_by_id,
      toss_choice,
      status,
      winner_id,
      winning_point,
      golden_point,
      start_time,
      end_time,
      live_stream_url,
      match_label,
      created_at,
      sets (
        id,
        set_number,
        score_a,
        score_b,
        winner_id,
        started_at,
        ended_at,
        current_serving_team_id,
        current_server_id
      )
    `)
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded')
    .order('match_number', { ascending: false });

  if (error) throw error;
  return data;
}
